import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { imageUrl } from '../lib/images';

export default function ServicesIndexPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEmergency, setFilterEmergency] = useState(false);

  useEffect(() => {
    fetch('/_api/public/content/ydelser')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente ydelser');
        return res.json();
      })
      .then((data) => {
        const items = Array.isArray(data) ? data : data.items || [];
        // sort by order
        items.sort((a, b) => (Number(a.order) || 99) - (Number(b.order) || 99));
        setServices(items);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Fejl ved indlæsning af ydelser:', err);
        setLoading(false);
      });
  }, []);

  const filteredServices = filterEmergency
    ? services.filter((s) => s.is_emergency_247)
    : services;

  return (
    <>
      <SEOHead
        title="Vores ydelser | Autohjælp & autotransport | MC Transport"
        description="Oversigt over alle ydelser hos MC Transport: Autotransport, autohjælp 24/7, bjærgningskøretøj, fritrækning, krantransport, sengetrailer og godstransport."
      />

      {/* Hero */}
      <section className="bg-zinc-950 text-white py-16 lg:py-20 border-b border-zinc-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(220,38,38,0.2),transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <span className="inline-block text-xs uppercase font-bold tracking-widest text-red-500 font-condensed mb-2">
            Landsdækkende Vognmand · Døgnvagt 24/7
          </span>
          <h1 className="font-condensed text-4xl sm:text-6xl font-extrabold uppercase tracking-tight text-white">
            Vi tilbyder
          </h1>
          <p className="mt-4 text-base sm:text-lg text-zinc-300 max-w-2xl mx-auto">
            <Link to="/" className="font-semibold text-white hover:text-red-400 underline decoration-red-500 underline-offset-2 transition-colors">MC Transport</Link> tilbyder alle former for <Link to="/ydelser/autotransport-ydelser" className="text-zinc-200 hover:text-white underline decoration-zinc-400 underline-offset-2">autotransport</Link>, døgnåben <Link to="/autohjaelp" className="text-zinc-200 hover:text-white underline decoration-zinc-400 underline-offset-2">autohjælp 24/7</Link>, <Link to="/bjaergning" className="text-zinc-200 hover:text-white underline decoration-zinc-400 underline-offset-2">bjærgning</Link> og specialtransport for private og erhverv i hele Danmark og Europa.
          </p>

          {/* Filter Pills */}
          <div className="mt-8 flex items-center justify-center gap-3">
            <button
              onClick={() => setFilterEmergency(false)}
              className={`px-4 py-2 rounded text-xs sm:text-sm font-bold uppercase tracking-wider transition-colors ${
                !filterEmergency
                  ? 'bg-red-600 text-white shadow'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
              }`}
            >
              Alle ydelser ({services.length})
            </button>
            <button
              onClick={() => setFilterEmergency(true)}
              className={`px-4 py-2 rounded text-xs sm:text-sm font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${
                filterEmergency
                  ? 'bg-red-600 text-white shadow'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
              <span>Kun Akut 24/7</span>
            </button>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 bg-zinc-100 min-h-[500px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20 text-zinc-500 font-mono text-sm">
              <i className="fa-solid fa-spinner fa-spin text-2xl mb-2 text-red-600" aria-hidden="true" />
              <p>Indlæser ydelser fra CMS...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map((service) => {
                const img = service.hero_image?.[0] || 'https://assets.auxodev.dk/assets/7ab2fd9d-1ad5-42a7-a15d-22229bea7818/roed-volvo-autotransport-med-7-biler-18-03-2025-379.jpeg';
                const serviceSlug = service.slug?._ || service.slug || service.id;

                return (
                  <Link
                    key={service.id || serviceSlug}
                    to={`/ydelser/${serviceSlug}`}
                    className="group flex flex-col bg-white rounded-lg overflow-hidden border border-zinc-200 shadow-sm hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1"
                  >
                    <div className="relative h-56 bg-zinc-950 overflow-hidden">
                      <img
                        src={imageUrl(img, 'width=600,height=400,fit=cover,quality=80,format=auto')}
                        alt={`${service.title} - MC Transport ydelse`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 brightness-90"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

                      {service.badge && (
                        <div className="absolute top-3 left-3">
                          <span
                            className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded shadow tracking-wider ${
                              service.badge.includes('24/7') || service.badge.includes('Akut')
                                ? 'bg-red-600 text-white'
                                : 'bg-zinc-900/90 text-white border border-white/20'
                            }`}
                          >
                            {service.badge}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="p-6 flex-1 flex flex-col justify-between">
                      <div>
                        <h2 className="font-condensed text-2xl font-bold uppercase text-zinc-950 group-hover:text-red-600 transition-colors">
                          {service.title}
                        </h2>
                        <p className="mt-2 text-xs sm:text-sm text-zinc-600 line-clamp-3 leading-relaxed">
                          {service.short_description || service.description?.replace(/<[^>]+>/g, '') || ''}
                        </p>
                      </div>

                      <div className="mt-6 pt-4 border-t border-zinc-100 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-red-600">
                        <span>Læs mere om ydelsen</span>
                        <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Emergency Bottom Callout */}
      <section className="bg-zinc-900 text-white py-12 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white">
            Har du en akut transportopgave eller brug for vejhjælp?
          </h2>
          <p className="mt-2 text-sm sm:text-base text-zinc-300">
            Tøv ikke med at ringe til vores vagtcentral. Vi sidder klar døgnet rundt i hele Danmark.
          </p>
          <div className="mt-6">
            <a
              href="tel:+4570700800"
              className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-red-600 hover:bg-red-700 text-white font-condensed text-2xl font-bold uppercase rounded shadow-xl"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span>Ring: +45 70 700 800</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
